/**
 * The tab "Inquiries" of the page "Learn from sales" (lead decision S7, tab 2): one row per
 * inquiry that I2Q linked to what sales quoted, newest first, one page of 50 from the server.
 * The columns: received (with the subject, a fixed width), area, the customer of I2Q and
 * of F&O with a match icon, the sales person ("Not shown" for Germany and France), the
 * lines matched as a bar, the quotation and its F&O status, the sales order, how it was
 * linked with the certainty word, the learnings, and "Still open in I2Q". The person and
 * the lines matched come early, so they are in view at 1440 px.
 *
 * The date in the first cell is a link that opens the drawer of the inquiry (`?case=`).
 * A table on a wide screen, cards on a phone.
 */

import { Icon } from "@norican/icons";
import { Badge, Button, DataGrid, type DataGridColumnDef, Meter, Typography } from "@norican/react";

import { ColumnCards } from "@/components/column-cards";
import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { countNoun, formatDate } from "@/lib/format";
import { CUSTOMER_MATCH_ICON } from "@/lib/line-icons";

import { CELL_LABELS, INQUIRY_COLUMN_LABELS } from "./labels";
import type { InquiryRow } from "./normalize";
import {
  agreementPercent,
  certaintyLabel,
  certaintyTone,
  customerMatchLabel,
  customerText,
  dayOf,
  personText,
  quoteStatusLabel,
  quoteStatusTone,
  type SalesTruthCustomerMatch,
  shareText,
  sourceLabel,
} from "./view";

/** The customer of I2Q and of F&O, with the icon of how they compare. */
export function CustomerMatch({ row }: { row: InquiryRow }) {
  const match = (row.customer_match ?? "no_fo") as SalesTruthCustomerMatch;
  const icon = CUSTOMER_MATCH_ICON[match] ?? CUSTOMER_MATCH_ICON.no_fo;
  return (
    <span className="flex items-start gap-1.5" data-customer-match={match}>
      <Icon
        icon={icon}
        size="sm"
        label={customerMatchLabel(match)}
        className="text-fg-muted mt-0.5 shrink-0"
      />
      <span className="flex min-w-0 flex-col">
        <span className="break-words">{`I2Q: ${customerText(row.i2q_customer, row.i2q_customer_name)}`}</span>
        <span className="break-words">
          {`F&O: ${customerText(
            row.fo_customer,
            // F&O carries the account only; the same account has the same name
            row.customer_match === "same" ? row.i2q_customer_name : null,
          )}`}
        </span>
      </span>
    </span>
  );
}

/** The line agreement as a bar with its per cent and the number of quoted lines. */
export function AgreementBar({
  share,
  lines,
}: {
  share: number | null | undefined;
  lines: number | null | undefined;
}) {
  if (share == null) {
    return <span className="text-fg-muted">{CELL_LABELS.notKnown}</span>;
  }
  return (
    <span className="flex min-w-24 flex-col gap-1" data-agreement={agreementPercent(share)}>
      <Meter.Root value={agreementPercent(share)} size="sm" tone="info">
        <Meter.Track>
          <Meter.Indicator />
        </Meter.Track>
      </Meter.Root>
      <span className="text-xs tabular-nums">
        {lines != null ? `${shareText(share)}, ${lines} ${CELL_LABELS.lines}` : shareText(share)}
      </span>
    </span>
  );
}

/** The link: how I2Q found it, with its certainty word. */
export function LinkCell({ source, certainty }: { source: string; certainty: string }) {
  return (
    <span className="flex flex-col items-start gap-1">
      <span className="break-words">{sourceLabel(source)}</span>
      <Badge tone={certaintyTone(certainty)} variant="subtle" size="sm" data-certainty={certainty}>
        {certaintyLabel(certainty)}
      </Badge>
    </span>
  );
}

/** The columns of the tab; `onOpen` opens the drawer of a row. */
export function inquiryColumns(onOpen: (row: InquiryRow) => void) {
  const columns: DataGridColumnDef<InquiryRow>[] = [
    {
      id: "received",
      header: INQUIRY_COLUMN_LABELS.received,
      cell: (row) => (
        // DS-GAP(datagrid-row-activate): DataGrid has no row click, so the first cell is
        // the button that opens the drawer of the row.
        <span className="flex max-w-56 flex-col items-start gap-0.5">
          <Button
            size="sm"
            variant="ghost"
            tone="accent"
            className="text-accent-fg-subtle h-auto px-0 py-0 text-left underline-offset-2 hover:underline"
            onClick={() => onOpen(row)}
            data-lfs-open-case={row.case_id}
          >
            {row.received_at ? formatDate(dayOf(row.received_at)) : row.case_id}
          </Button>
          {row.subject && (
            <Typography
              as="span"
              variant="caption"
              tone="muted"
              className="line-clamp-2 break-words"
            >
              {row.subject}
            </Typography>
          )}
        </span>
      ),
    },
    { id: "area", header: INQUIRY_COLUMN_LABELS.area, cell: (row) => row.area.toUpperCase() },
    {
      id: "customer",
      header: INQUIRY_COLUMN_LABELS.customer,
      cell: (row) => <CustomerMatch row={row} />,
    },
    {
      id: "person",
      header: INQUIRY_COLUMN_LABELS.person,
      cell: (row) => (
        <span
          className={row.person_hidden ? "text-fg-muted" : undefined}
          data-person-hidden={row.person_hidden ? "" : undefined}
        >
          {personText(row)}
        </span>
      ),
    },
    {
      id: "agreement",
      header: INQUIRY_COLUMN_LABELS.agreement,
      cell: (row) => <AgreementBar share={row.line_agreement} lines={row.line_count} />,
    },
    {
      id: "quotation",
      header: INQUIRY_COLUMN_LABELS.quotation,
      cell: (row) =>
        row.quotation ? (
          <span className="flex flex-col items-start gap-1">
            <span className="break-all">{row.quotation}</span>
            {row.quote_status && (
              <Badge tone={quoteStatusTone(row.quote_status)} variant="outline" size="sm">
                {quoteStatusLabel(row.quote_status)}
              </Badge>
            )}
          </span>
        ) : (
          CELL_LABELS.none
        ),
    },
    {
      id: "order",
      header: INQUIRY_COLUMN_LABELS.order,
      cell: (row) => <span className="break-all">{row.sales_order ?? CELL_LABELS.none}</span>,
    },
    {
      id: "linked",
      header: INQUIRY_COLUMN_LABELS.linked,
      cell: (row) => <LinkCell source={row.source} certainty={row.certainty} />,
    },
    {
      id: "learnings",
      header: INQUIRY_COLUMN_LABELS.learnings,
      align: "end",
      cell: (row) => (
        <span className="flex flex-col items-end tabular-nums">
          <span>{row.learnings}</span>
          {row.conflicts > 0 && (
            <Typography as="span" variant="caption" className="text-warning-fg">
              {countNoun(row.conflicts, CELL_LABELS.conflict, CELL_LABELS.conflicts)}
            </Typography>
          )}
        </span>
      ),
    },
    {
      id: "open",
      header: INQUIRY_COLUMN_LABELS.open,
      cell: (row) =>
        row.open_in_i2q ? (
          <Badge tone="warning" variant="subtle" size="sm" data-open-in-i2q="">
            {CELL_LABELS.openInI2q}
          </Badge>
        ) : (
          ""
        ),
    },
  ];
  return columns;
}

/** The rows as a `DataGrid` on a wide screen, as cards on a phone. */
export function InquiryTable({
  rows,
  onOpen,
}: {
  rows: readonly InquiryRow[];
  onOpen: (row: InquiryRow) => void;
}) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const columns = inquiryColumns(onOpen);
  const getRowId = (row: InquiryRow) => row.case_id;
  if (narrow) {
    return (
      <ColumnCards
        columns={columns}
        data={rows}
        getRowId={getRowId}
        lead={["received"]}
        label={INQUIRY_COLUMN_LABELS.rows}
      />
    );
  }
  return (
    <div className="min-w-0 overflow-x-auto">
      <DataGrid<InquiryRow>
        columns={columns}
        data={[...rows]}
        getRowId={getRowId}
        density="compact"
        aria-label={INQUIRY_COLUMN_LABELS.rows}
        className="min-w-[1100px]"
      />
    </div>
  );
}
